import React, { FunctionComponent, useEffect } from "react";
import { useHistory } from "react-router-dom";
import pulseLogo from "./../../assets/images/pulse-logo.svg";
import pulseTitleLogo from "./../../assets/images/pulse-title-logo.svg";
import bgLeftImage from "./../../assets/images/bg-left.svg";
import bgRightImage from "./../../assets/images/bg-right.svg";
import "./AdminDashboard.scss";
import { useOktaAuth } from "@okta/okta-react";
import LoadingWBackdrop from "../../components/common/LoadingWBackdrop";
import { NoResultsFound } from "../../components/common/NoResultsFound";
import { HeroBanner } from "../../components/banner/HeroBanner";
import { TILES } from "./tileConfig";
import { usePressTransition } from "../../hooks/usePressTransition";

const AdminDashboard: FunctionComponent = () => {

    const [tilesToRender, setTilesToRender] = React.useState<Array<any>>([]);
    const { authState } = useOktaAuth();
    const history = useHistory();
    const { isPressed, triggerPress } = usePressTransition<string | number>();

    useEffect(() => {
        setTilesToRender(TILES.filter(tile => {
            const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
            return (userDetails.hasOwnProperty(tile.permission) && userDetails[tile.permission] === true) || tile.upcoming === true;
        }));
    }, []);

    const handleTileClick = (event: React.MouseEvent | React.KeyboardEvent, url: string, tileId: string | number) => {
        const target = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
        triggerPress(tileId, () => {
            history.push(url);
            target?.blur();
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent, url: string, tileId: string | number) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleTileClick(e, url, tileId);
        }
    };

    const renderNoAppTile = () => {
        return (
            <div className="no-app-tile">
                <NoResultsFound
                    className="no-app-header"
                    message="No applications available"
                    description="It looks like there are no applications configured for your account. Please check your settings or contact your administrator for assistance."
                />
            </div>
        );
    };

    if (!authState || authState.isPending) {
        return <LoadingWBackdrop />;
    }

    if (!authState.isAuthenticated) {
        // Render nothing while redirecting
        return null;
    }

    return (
        <div className="app-dashboard-container">
            <HeroBanner title="Welcome To Panviva Pulse" titleLogoSrc={pulseTitleLogo} desc="The heartbeat of your knowledge, healthy, unified, and ready to power your success." mainLogoSrc={pulseLogo} />
            {tilesToRender.length === 0 ? renderNoAppTile() :
                <ul className="app-tile-container" data-testid="app-tile-container">
                    {tilesToRender.map((tile) => {
                        return (
                            <li
                                key={tile.id}
                                className={`app-tile-item${tile.disable ? " app-tile-item--disabled" : ""}${isPressed(tile.id) ? " app-tile-item--pressed" : ""}`}
                                onClick={(e) => !tile.disable && handleTileClick(e, tile.route, tile.id)}
                                onKeyDown={(e) => !tile.disable && handleKeyDown(e, tile.route, tile.id)}
                                role={tile.disable ? undefined : "button"}
                                tabIndex={tile.disable ? -1 : 0}
                                aria-label={`${tile.name}, ${tile.desc}`}
                                aria-disabled={tile.disable}
                                style={{ cursor: tile.disable ? "default" : "pointer" }}
                            >
                                {tile.upcoming && <div className="upcoming-badge">COMING SOON</div>}
                                <div className="logo-container">
                                    <img className="up-panv-logo" src={tile.imgSrc} alt={`${tile.name} icon`} />
                                </div>
                                <div className="app-tile-item-footer">{`${tile.name}`}
                                    <div className="app-tile-item-desc">{tile.desc}</div>
                                </div>
                            </li>
                        );
                    })}
                </ul>}
            <div className="dashboard-background-images">
                <img src={bgLeftImage} alt="Background Left" className="bg-left" />
                <img src={bgRightImage} alt="Background Right" className="bg-right" />
            </div>
        </div>
    );
}

export default AdminDashboard;
